/**
 * contractorsList — the roster list's contract: where it lives, what columns
 * the app reads, and how one row travels between sites.
 *
 * PURE ON PURPOSE. Two things have to agree exactly — the read in
 * WorkcentersService and the provisioning in RosterSiteMigrationService — and
 * a $select naming one column a list does not have is rejected WHOLE by
 * SharePoint: the read 400s rather than degrading, so a copy that missed a
 * column would not weaken the roster, it would take it out. Keeping the
 * contract in one file with no I/O in it means the agreement can be tested
 * without a tenant anywhere near it.
 */
export const CONTRACTORS_LIST = "iSyn_Contractors";
export const CONTRACTORS_SELECT =
  "Id,Title,displayName,code,tier,contractorStatus,tenure,capacity,quality,speed,proAllowed,skuComp," +
  "Date_x0020_Started,Historical_x0020_Volume,Historical_x0020_Designs,Eligible_x0020_For_x0020_Assign,Monthly_x0020_Cap_x0020_Target";

export interface ILegacyContractorRow {
  Id: number;
  Title: string | null;
  displayName: string | null;
  code: string | null;
  tier: string | null;
  contractorStatus: string | null;
  tenure: number | null;
  capacity: number | null;
  quality: number | null;
  speed: number | null;
  proAllowed: boolean | null;
  skuComp: string | null;
  Date_x0020_Started: string | null;
  Historical_x0020_Volume: number | null;
  Historical_x0020_Designs: string | null;
  Eligible_x0020_For_x0020_Assign: boolean | null;
  Monthly_x0020_Cap_x0020_Target: number | null;
}

/** SharePoint's own numbering for the column types used here. */
export const FIELD_KIND = { text: 2, note: 3, datetime: 4, boolean: 8, number: 9 } as const;

export type FieldKind = keyof typeof FIELD_KIND;

/**
 * Every column the roster read names, and the title that produces its internal
 * name.
 *
 * SharePoint derives the internal name from the title at creation and encodes
 * a space as `_x0020_`, which is why the last five are titled with spaces: a
 * column created as "Date_x0020_Started" would come out internally as
 * `Date_x0020_x0020_Started` and the read would never find it. `Id` and
 * `Title` are built into every list and are not created.
 */
export const CONTRACTOR_FIELDS: readonly {
  title: string; internal: string; kind: FieldKind; indexed?: boolean;
}[] = [
  { title: "displayName", internal: "displayName", kind: "text" },
  { title: "code", internal: "code", kind: "text", indexed: true },
  { title: "tier", internal: "tier", kind: "text" },
  { title: "contractorStatus", internal: "contractorStatus", kind: "text", indexed: true },
  { title: "tenure", internal: "tenure", kind: "number" },
  { title: "capacity", internal: "capacity", kind: "number" },
  { title: "quality", internal: "quality", kind: "number" },
  { title: "speed", internal: "speed", kind: "number" },
  { title: "proAllowed", internal: "proAllowed", kind: "boolean" },
  // Multi-line: both of these hold a JSON array of design codes, which runs
  // past what a single-line column keeps — and a truncated array does not
  // parse, so the sewer reads as having sewn nothing.
  { title: "skuComp", internal: "skuComp", kind: "note" },
  { title: "Date Started", internal: "Date_x0020_Started", kind: "datetime" },
  { title: "Historical Volume", internal: "Historical_x0020_Volume", kind: "number" },
  { title: "Historical Designs", internal: "Historical_x0020_Designs", kind: "note" },
  { title: "Eligible For Assign", internal: "Eligible_x0020_For_x0020_Assign", kind: "boolean" },
  { title: "Monthly Cap Target", internal: "Monthly_x0020_Cap_x0020_Target", kind: "number" },
];

/**
 * The key two rows are the same person by.
 *
 * The payroll code where there is one; the name where there is not, which is
 * how the old list carries its newest few (ABCEDE and AZORES both have a blank
 * code today). Getting this wrong in either direction is bad in a different
 * way: too loose merges two people, too strict copies somebody twice.
 */
export function contractorKey(
  r: { code?: string | null; Title?: string | null; displayName?: string | null }
): string {
  const code = (r.code ?? "").trim().toUpperCase();
  if (code) return `CODE:${code}`;
  return `NAME:${((r.displayName ?? r.Title) ?? "").trim().toUpperCase()}`;
}

/**
 * One row as it goes to the new list.
 *
 * Blanks are dropped rather than written: a blank is a blank either way, and
 * null for a date or a number is the one shape SharePoint rejects outright —
 * which would fail the copy on exactly the contractors who are missing a
 * field. `false` and `0` are values, not blanks, and are kept.
 */
export function contractorBody(row: ILegacyContractorRow): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  const put = (key: string, v: unknown): void => {
    if (v === null || v === undefined || v === "") return;
    body[key] = v;
  };
  put("Title", row.Title ?? row.displayName);
  for (const f of CONTRACTOR_FIELDS) {
    put(f.internal, (row as unknown as Record<string, unknown>)[f.internal]);
  }
  return body;
}

/**
 * What the two sites disagree about.
 *
 * WHY THIS EXISTS AT ALL. The copy reported "103 already there, 0 copied",
 * which proves the two lists hold the same PEOPLE — every key on the old site
 * was already on Manufacturing — and proves nothing whatsoever about their
 * VALUES. Manufacturing's copy could be a year-old snapshot with the same
 * names and stale tiers, capacities and eligibility, and the roster now scores
 * off it. Same names, different numbers, no error anywhere: exactly the shape
 * of wrong that gets found weeks later in a ranking nobody trusts.
 *
 * BLANK IS NOT A DIFFERENCE FROM BLANK. Null, undefined and "" all mean "the
 * cell is empty" across a SharePoint read, and reporting those against each
 * other would bury the real differences in noise.
 *
 * Numbers compare numerically and dates by their DAY: the same value can come
 * back as 25.4 and "25.4", and the same date as midnight in two different
 * offsets. Neither is a disagreement anybody needs to see.
 */
export interface IRosterFieldDiff {
  name: string;
  field: string;
  legacy: string;
  mfg: string;
}

export interface IRosterDiff {
  /** On the old site only — a contractor the copy has not carried across. */
  onlyLegacy: string[];
  /** On Manufacturing only — added there, or a name that has since changed. */
  onlyMfg: string[];
  differing: IRosterFieldDiff[];
  /** People present on both with nothing to report. */
  identical: number;
}

const blank = (v: unknown): boolean => v === null || v === undefined || String(v).trim() === "";

function sameValue(kind: FieldKind, a: unknown, b: unknown): boolean {
  if (blank(a) && blank(b)) return true;
  if (blank(a) !== blank(b)) return false;
  if (kind === "number") {
    const x = Number(a), y = Number(b);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return String(a) === String(b);
    // Four decimals: the scores carry two, and a float that survived a round
    // trip through JSON is not a difference anybody can act on.
    return Math.abs(x - y) < 0.0001;
  }
  if (kind === "boolean") return Boolean(a) === Boolean(b);
  if (kind === "datetime") return String(a).slice(0, 10) === String(b).slice(0, 10);
  return String(a).trim() === String(b).trim();
}

const nameOf = (r: ILegacyContractorRow): string =>
  ((r.displayName ?? r.Title) ?? "").trim() || `#${r.Id}`;

export function diffRosters(
  legacy: readonly ILegacyContractorRow[], mfg: readonly ILegacyContractorRow[]
): IRosterDiff {
  const byKey = new Map(mfg.map(r => [contractorKey(r), r]));
  const seen = new Set<string>();
  const out: IRosterDiff = { onlyLegacy: [], onlyMfg: [], differing: [], identical: 0 };

  for (const l of legacy) {
    const key = contractorKey(l);
    const m = byKey.get(key);
    if (!m) { out.onlyLegacy.push(nameOf(l)); continue; }
    seen.add(key);
    let clean = true;
    for (const f of CONTRACTOR_FIELDS) {
      const a = (l as unknown as Record<string, unknown>)[f.internal];
      const b = (m as unknown as Record<string, unknown>)[f.internal];
      if (sameValue(f.kind, a, b)) continue;
      clean = false;
      out.differing.push({
        name: nameOf(l),
        field: f.title,
        legacy: blank(a) ? "—" : String(a),
        mfg: blank(b) ? "—" : String(b),
      });
    }
    if (clean) out.identical += 1;
  }
  for (const m of mfg) if (!seen.has(contractorKey(m))) out.onlyMfg.push(nameOf(m));
  return out;
}
